/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { ShopperSearch } from '@/scapi';
import { Link } from '@/components/link';
import { Icon } from '@/components/icon';
import { ProductImage } from '@/components/product-image/product-image';
import ProductPrice from '@/components/product-price';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { createProductUrl, getImagesForColor } from '@/lib/product/product-utils';
import { readHitModelCode, readHitModelNameForWeb } from '@/lib/product/custom-attributes';
import { cn } from '@/lib/utils';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

/**
 * The thumbnail is a fixed 96×48 box (2:1), so DIS is asked for a thumbnail rather than a hero.
 * `DynamicImage` requests 1x and 2x from this, so 96px covers the retina case too.
 */
const THUMBNAIL_WIDTHS = ['96px'];

/**
 * Collapse a column to the width of its own content.
 *
 * The `tables` primitive is `w-full` with **no** `table-fixed` (`table-styles.ts`), so the
 * browser's automatic algorithm measures every column's content and then hands the surplus
 * — everything `w-full` adds beyond the content total — to the columns that declared no
 * width. With Name, Model and Price all undeclared, all three inflated, leaving Model and
 * Price several times wider than the values in them.
 *
 * `w-px` is the fix rather than a real number: under `table-layout: auto` a cell width is a
 * *hint*, and the browser will never render a column narrower than its minimum content
 * width. Asking for 1px therefore resolves to exactly "as narrow as the content allows",
 * with no measurement to keep in sync when a label or a currency format changes. It works
 * only because these cells do not wrap — `wrap: false` (the default) supplies
 * `whitespace-nowrap`, so minimum content width is the full value on one line. A column
 * with `wrap` would collapse to its longest *word* instead.
 *
 * Name is left undeclared on purpose: it is the one column that should take the slack.
 */
const SHRINK_TO_CONTENT = 'w-px';

/**
 * `components/label-sm` from the design for the header labels: 14px at the body weight with
 * **10%** tracking (1.4px on 14px) and capitalized, in text-color-secondary.
 *
 * The `tables` primitive ships `tracking-[0.03em]`, which is right for body cells but not for
 * these labels. Overridden per instance rather than in the primitive, because that would change
 * every table in the app and the primitive belongs to CRCB2B-636.
 *
 * Note the design also specifies `line-height: 20px`; `text-sm` resolves to 21px via
 * `--text-sm--line-height: 1.5`. Left alone — a 1px difference inside a fixed 48px header row is
 * not worth forking the primitive's type ramp over.
 */
const HEADER_LABEL = 'tracking-[0.1em] capitalize';

/**
 * Placeholder rows.
 *
 * Used for BOTH loading shapes, which is the point of extracting it: the whole-table state (no
 * products yet, or an explicit `isLoading`) and the streaming tail appended AFTER real rows while
 * the non-critical promise resolves. `DeferredProductGrid` passes `skeletonCount` in that second
 * case and expects the space to be reserved — `ProductGrid` does the same thing with
 * `ProductTileSkeleton` (grid.tsx). An earlier version only handled the first shape, via an early
 * return, so with critical results already on screen the count was silently ignored and the table
 * grew by N rows when the rest arrived (CLS — see the performance rules in CLAUDE.md).
 *
 * The bar widths are not decorative: with `w-px` headers a column collapses to the widest of its
 * label and its content, so these keep the loading columns close to the resolved ones and stop the
 * table reflowing sideways as rows arrive.
 */
function SkeletonRows({ count, keyPrefix = 'list-skeleton' }: { count: number; keyPrefix?: string }): ReactElement {
    return (
        <>
            {Array.from({ length: count }, (_, index) => (
                <TableRow key={`${keyPrefix}-${index}`} interactive={false}>
                    <TableCell>
                        <Skeleton className="size-12" />
                    </TableCell>
                    <TableCell>
                        <Skeleton className="h-4 w-48" />
                    </TableCell>
                    {/* Centred with `mx-auto`, not `text-center`: a Skeleton is a block, so it is
                        the box that has to be centred, not inline content inside it. */}
                    <TableCell>
                        <Skeleton className="mx-auto h-4 w-28" />
                    </TableCell>
                    <TableCell>
                        <Skeleton className="mx-auto h-4 w-16" />
                    </TableCell>
                    <TableCell>
                        <Skeleton className="h-4 w-12" />
                    </TableCell>
                </TableRow>
            ))}
        </>
    );
}

/**
 * List presentation for product results — a data table (CRCB2B-87).
 *
 * Consumes the promoted `tables` primitive (CRCB2B-636). Figma `14380:38735`, where the design
 * models grid and list as one component with a `Mode` variant, `Mode=table` being this.
 *
 * ## Why a table and not stacked rows
 *
 * The AC says "row-based layout … horizontally structured", which reads as stacked rows. The
 * design is unambiguously a table: grey header row, per-column alignment, a trailing action
 * column. Per the tie-break recorded on CRCB2B-348, the design governs.
 *
 * ## Narrow widths scroll; they do not restack
 *
 * `overflow` is left at the primitive's `scroll` default, which also gives the scroll container
 * its keyboard tab stop. Restacking a row into a card is explicitly forbidden by the `tables`
 * spec — and a stacked table row would just be the grid again. Only the Name column opts into
 * `wrap`; price and model identifier are structured values that must stay on one line.
 *
 * ## No badges
 *
 * The design shows none in this mode. Not an oversight to be corrected later.
 */
export default function ProductList({
    critical,
    nonCritical,
    handleProductClick,
    isLoading = false,
    skeletonCount,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: ProductSearchHit[];
    handleProductClick?: (product: ProductSearchHit) => void;
    isLoading?: boolean;
    skeletonCount?: number;
}): ReactElement {
    const { t } = useTranslation('common');
    const config = useConfig();
    const { currency } = useSite();

    const products = [...(critical ?? []), ...(nonCritical ?? [])];
    const loadingRowCount = Math.max(products.length, 4);

    const onRowClick = useCallback(
        (product: ProductSearchHit) => () => handleProductClick?.(product),
        [handleProductClick]
    );

    const columns = (
        <TableRow interactive={false}>
            <TableHead className={cn(HEADER_LABEL, 'w-32')}>
                <span className="sr-only">{t('productListing.table.image')}</span>
            </TableHead>
            {/* The only column with no declared width, so it absorbs all the surplus the
                table's `w-full` has to distribute. See `SHRINK_TO_CONTENT` below. */}
            <TableHead className={HEADER_LABEL}>{t('productListing.table.name')}</TableHead>
            <TableHead className={cn(HEADER_LABEL, SHRINK_TO_CONTENT, 'text-center')}>
                {t('productListing.table.modelId')}
            </TableHead>
            {/* TODO(CRCB2B-87): "Bronze Price" is the design's mock tier. The site import
                defines two price books and no tier concept, so there is nothing to resolve it
                from — the string is translated so swapping in real tier names later is a data
                change rather than a markup one. */}
            <TableHead className={cn(HEADER_LABEL, SHRINK_TO_CONTENT, 'text-center')}>
                {t('productListing.table.price')}
            </TableHead>
            <TableHead className={cn(HEADER_LABEL, 'w-24')}>
                <span className="sr-only">{t('productListing.table.view')}</span>
            </TableHead>
        </TableRow>
    );

    if (isLoading || (products.length === 0 && (skeletonCount ?? 0) > 0)) {
        const count = isLoading ? loadingRowCount : (skeletonCount ?? 0);
        return (
            <Table containerProps={{ 'aria-label': t('productListing.table.label') }} aria-busy={isLoading}>
                <TableHeader>{columns}</TableHeader>
                <TableBody data-testid="product-list-loading-state">
                    <SkeletonRows count={count} />
                </TableBody>
            </Table>
        );
    }

    if (products.length === 0) {
        return (
            <div className="py-12 text-center">
                <p className="text-sm text-muted-foreground">{t('noProductsFound')}</p>
            </div>
        );
    }

    const pendingRowCount = skeletonCount ?? 0;

    return (
        <Table
            containerProps={{ 'aria-label': t('productListing.table.label') }}
            // `relative` makes the scroll container a CONTAINING BLOCK, and it is what stops the
            // whole page scrolling sideways on mobile.
            //
            // `overflow-x-auto` clips normal descendants, but an absolutely positioned element is
            // only clipped by ancestors in its CONTAINING-BLOCK chain — and with nothing
            // positioned between here and <body>, every `sr-only` span in the table resolved its
            // containing block to the initial one. `sr-only` is `position:absolute`, so those
            // spans (ProductPrice's `aria-live` announcement, and the sr-only header labels) sat
            // at their static x — up to 536px on a 375px viewport — and contributed that to the
            // ROOT's scrollable overflow while the table itself was correctly clipped. Measured
            // in Chromium at 375px: `documentElement.scrollWidth` 536 vs `body.scrollWidth` 375,
            // window scrollable by 161px. With `relative` here: 375 and 0, inner scroll intact.
            //
            // This is latent in the `tables` primitive for ANY consumer whose cells carry
            // sr-only text and whose table is wide enough to overflow — raised for CRCB2B-636
            // rather than patched there, since that would change every table in the app.
            containerClassName="relative"
            // Still busy while the streaming tail is outstanding: rows are on screen but the set
            // is incomplete, which is exactly what `aria-busy` describes.
            aria-busy={pendingRowCount > 0 || undefined}>
            <TableHeader>{columns}</TableHeader>
            <TableBody>
                {products.map((product) => {
                    // Mirror the grid tile's URL exactly (`product-tile/index.tsx`). For a master
                    // product the tile appends `?pid=<representedProduct.id>` so the PDP opens on
                    // the represented variant; without it the same product opened in a different
                    // state depending on which view the shopper happened to be in. Bundles and
                    // sets are excluded because `representedProduct` is not a selectable variant
                    // for them — the same condition the tile applies.
                    const isMaster = !!product.variants;
                    const isBundleOrSet = Boolean(product.productType?.bundle || product.productType?.set);
                    const variantPid = isMaster && !isBundleOrSet ? (product.representedProduct?.id ?? null) : null;
                    const productUrl = createProductUrl(product.productId ?? '', null, 'color', variantPid);
                    // CRCB2B-385, mirroring the grid tile: master-level merchant attributes for
                    // display, falling back to the typed fields when unset. `productUrl` above
                    // stays on `productId`.
                    const productName = readHitModelNameForWeb(product) ?? product.productName ?? '';
                    const modelCode = readHitModelCode(product) ?? product.productId;
                    const image = getImagesForColor(product, null, 'medium')[0] ?? product.image;
                    const imageUrl = image?.disBaseLink || image?.link;

                    return (
                        <TableRow key={product.productId} interactive>
                            <TableCell>
                                {/* A fixed 2:1 bordered frame with the image CONTAINED and
                                    centred, on a light fill — per the design's
                                    `border … background: … lightgray 50% / contain no-repeat`.
                                    Product shots are tall and narrow, so a square box let them
                                    overflow; `object-contain` inside a fixed aspect box is what
                                    keeps every row the same height regardless of source ratio. */}
                                <div className="relative aspect-[2/1] w-24 overflow-hidden border border-border bg-muted">
                                    {imageUrl && (
                                        <ProductImage
                                            src={imageUrl}
                                            alt={image?.alt || productName}
                                            className="absolute inset-0 size-full"
                                            // The sizing and object-fit MUST go on `imageProps`, not
                                            // on `className`. `className` lands on DynamicImage's
                                            // wrapper <div> (dynamic-image/index.tsx:412), and the
                                            // <img> inside <picture> is given nothing but a
                                            // hardcoded `object-cover` — no width, no height. So an
                                            // `object-contain` passed via `className` sits on a
                                            // <div>, where object-fit does not apply at all, while
                                            // the unconstrained <img> lays out at its intrinsic
                                            // size and overflows the frame (94x112 in a 94x46 box).
                                            // `imageProps.className` is merged through `cn` with
                                            // that `object-cover` (index.tsx:374), so `size-full`
                                            // gives the image a box and `object-contain` wins the
                                            // merge. `overflow-hidden` above is belt-and-braces.
                                            imageProps={{ className: 'size-full object-contain' }}
                                            widths={THUMBNAIL_WIDTHS}
                                        />
                                    )}
                                </div>
                            </TableCell>
                            {/* The only column that may wrap: a product name is the value
                                where truncation actually loses information. */}
                            <TableCell wrap>
                                <Link
                                    to={productUrl}
                                    onClick={onRowClick(product)}
                                    className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1">
                                    {productName}
                                </Link>
                            </TableCell>
                            <TableCell className="text-center text-muted-foreground">{modelCode}</TableCell>
                            <TableCell className="text-center">
                                <ProductPrice
                                    type="unit"
                                    product={product}
                                    currency={currency ?? config.commerce.sites?.[0]?.defaultCurrency ?? ''}
                                    labelForA11y={productName || product.productId || ''}
                                    // The design shows price only in this column, and a promo
                                    // callout here actively breaks the layout: this cell is
                                    // `whitespace-nowrap` with `h-16 overflow-hidden`
                                    // (`wrap:false`) inside a column deliberately collapsed to
                                    // its content (`SHRINK_TO_CONTENT`). A callout like "Free
                                    // shipping on orders over $99" cannot wrap under the
                                    // inherited `nowrap`, so it contributes its full one-line
                                    // width to a column sized for "$939.00" — squeezing Name —
                                    // and is then vertically clipped by `h-16` anyway. The grid
                                    // tile shows callouts because its card has room to; a table
                                    // cell does not.
                                    hidePromo
                                    // 14px at 150% (both from `text-sm`), body weight, 3%
                                    // tracking (0.42/14), text-color-primary. `mt-0` cancels the
                                    // Typography margin, which is meant for stacked prose rather
                                    // than a table cell. No alignment — the cell centres it.
                                    //
                                    // `font-book` is currently INERT and that is expected.
                                    // CurrentPrice still hardcodes `font-semibold` and appends
                                    // the caller's className by string concatenation, so both
                                    // weights reach the element unmerged and CSS source order
                                    // picks 600. CRCB2B-100 (MR !38) removes that hardcoded
                                    // weight — SRAM's type system defines only 400/450/500 — and
                                    // this declaration starts winning the moment that lands.
                                    // Deliberately NOT fixed here: duplicating another branch's
                                    // change would only conflict with it.
                                    currentPriceProps={{
                                        className:
                                            'text-sm font-book tracking-sram text-foreground [&:not(:first-child)]:mt-0',
                                    }}
                                    listPriceProps={{ className: 'text-muted-foreground text-sm line-through' }}
                                />
                            </TableCell>
                            <TableCell>
                                <Link
                                    to={productUrl}
                                    onClick={onRowClick(product)}
                                    // The row already exposes the name as a link; this one is
                                    // the design's explicit affordance. Named per-product so
                                    // a screen-reader list of links is not 24 items of "View".
                                    aria-label={t('productListing.table.viewProduct', {
                                        productName,
                                        defaultValue: `View ${productName}`,
                                    })}
                                    className="group/view inline-flex items-center gap-1 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1">
                                    {/* The underline is on the LABEL, not on the anchor.
                                        `text-decoration` propagates from an ancestor to
                                        everything in its line box, and a descendant cannot
                                        opt out — `no-underline` on the icon does nothing,
                                        because removing an inherited decoration is not
                                        something CSS allows a child to do. Our Icon renders a
                                        Material Symbols **font glyph** in a <span>, not an
                                        <svg>, so the anchor's underline was being drawn under
                                        the glyph too, at the glyph's own baseline rather than
                                        the label's — a short detached stroke beside the word.
                                        Hovering the anchor still drives it, via the named
                                        group, so the whole link stays one hover target. */}
                                    <span className="group-hover/view:underline">{t('productListing.table.view')}</span>
                                    <Icon name="arrow_forward" size={16} />
                                </Link>
                            </TableCell>
                        </TableRow>
                    );
                })}
                {/* The streaming tail. `DeferredProductGrid` renders this component with the
                    critical rows plus a `skeletonCount` for the non-critical products still in
                    flight; without these the table would grow by that many rows the moment the
                    promise resolved. Same contract the grid honours with `ProductTileSkeleton`. */}
                {pendingRowCount > 0 && <SkeletonRows count={pendingRowCount} keyPrefix="list-pending" />}
            </TableBody>
        </Table>
    );
}
